/**
 * The `x-xsrf-token` header for a same-origin `/api/*` call made with the browser's own `fetch`.
 *
 * The Wix gateway blocks a state-changing request that carries no `x-xsrf-token` matching the
 * `XSRF-TOKEN` cookie (403, `CsrfViolationException`, reason `NoHeader`), unless the request's
 * Referer/Origin vouches for it. A tab whose page sends a strict Referrer-Policy (the invite and
 * share-card pages) loses that second path, so every write it makes must carry the header.
 * `@wix/http-client` attaches it on its own; the native-`fetch` paths in `api-client.ts` use this.
 *
 * No runtime imports: tests drive it directly (orion-web/CLAUDE.md, "a unit test must not import the
 * app's dependency graph").
 */

const XSRF_COOKIE = 'XSRF-TOKEN';
const XSRF_HEADER = 'x-xsrf-token';
const READ_METHODS = new Set(['GET', 'HEAD']);

/** The `XSRF-TOKEN` value in a `document.cookie` string, decoded, or undefined. */
export const xsrfTokenFrom = (cookie: string): string | undefined => {
  for (const part of cookie.split(';')) {
    const [name, ...rest] = part.trim().split('=');
    if (name === XSRF_COOKIE) {
      return decodeURIComponent(rest.join('='));
    }
  }
  return undefined;
};

/** Any `HeadersInit` as a plain object, so callers can add to it without the `Headers` class. */
export const headersRecord = (headers: HeadersInit | undefined): Record<string, string> => {
  if (!headers) {
    return {};
  }
  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }
  if (typeof (headers as Headers).forEach === 'function') {
    const record: Record<string, string> = {};
    (headers as Headers).forEach((value, key) => {
      record[key] = value;
    });
    return record;
  }
  return { ...(headers as Record<string, string>) };
};

/**
 * `init` with `x-xsrf-token` added for a state-changing method. A read, a request that already names
 * the header (in any case), or a browser with no `XSRF-TOKEN` cookie gets `init` back unchanged.
 */
export const withXsrfHeader = (init: RequestInit, cookie: string): RequestInit => {
  const method = (init.method ?? 'GET').toUpperCase();
  if (READ_METHODS.has(method)) {
    return init;
  }
  const token = xsrfTokenFrom(cookie);
  if (!token) {
    return init;
  }
  const headers = headersRecord(init.headers);
  if (Object.keys(headers).some((key) => key.toLowerCase() === XSRF_HEADER)) {
    return init;
  }
  return { ...init, headers: { ...headers, [XSRF_HEADER]: token } };
};

/** The page's cookies, or '' where there is no document (SSR, tests). */
export const documentCookie = (): string => (typeof document === 'undefined' ? '' : document.cookie);
